import { FC, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import CircleDollar from 'dibs-icons/exports/legacy/CircleDollar';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';
import { trackPricingGuidanceComparePricesClick } from '../PdpSharedPricingGuidance/pricingGuidanceTracking';
import { showSiderailSoldPricingGuidance } from '../PdpSharedPricingGuidance/pricingGuidanceHelpers';
import { PdpSharedPricingGuidanceSoldLink } from '../PdpSharedPricingGuidance/PdpSharedPricingGuidanceSoldLink';

import { PdpRespSidebarBlurbPricingGuidance_item$key } from './__generated__/PdpRespSidebarBlurbPricingGuidance_item.graphql';
import { PdpRespSidebarBlurbPricingGuidance_viewer$key } from './__generated__/PdpRespSidebarBlurbPricingGuidance_viewer.graphql';

import styles from './PdpRespSidebarBlurbPricingGuidance.scss';

const viewerFragment = graphql`
    fragment PdpRespSidebarBlurbPricingGuidance_viewer on Viewer {
        ...pricingGuidanceHelpers_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespSidebarBlurbPricingGuidance_item on Item {
        pricingGuidance(isTrade: $isTrade) {
            priceRange
        }
        ...pricingGuidanceHelpers_item
        ...PdpSharedPricingGuidanceSoldLink_item
    }
`;

export const PdpRespSidebarBlurbPricingGuidance: FC<{
    viewer: PdpRespSidebarBlurbPricingGuidance_viewer$key;
    item: PdpRespSidebarBlurbPricingGuidance_item$key;
    currency: string;
    skuId?: string | null;
}> = ({ viewer: viewerRef, item: itemRef, currency, skuId }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const { pricingGuidance } = item;

    const onClick = useCallback(() => {
        trackPricingGuidanceComparePricesClick(pricingGuidance?.priceRange || null);
    }, [pricingGuidance]);

    return showSiderailSoldPricingGuidance({ viewer, item, currency }) ? (
        <PdpSharedBlurb zeroPadding IconComponent={CircleDollar} iconClass={styles.icon}>
            <FormattedMessage
                id="PdpRespSidebarBlurbPricingGuidance.link"
                defaultMessage="<bold>Pricing Guidance:</bold> View similar sold items <link>Compare Prices</link>"
                values={{
                    bold: chunks => <strong>{chunks}</strong>,
                    link: chunks => (
                        <PdpSharedPricingGuidanceSoldLink
                            item={item}
                            onClick={onClick}
                            skuId={skuId}
                        >
                            {chunks}
                        </PdpSharedPricingGuidanceSoldLink>
                    ),
                }}
            />
        </PdpSharedBlurb>
    ) : null;
};
